import { useMemo, useCallback } from "react"
import { Form, Row, Col, Label, Button, FormFeedback } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import {
  useCreateVehicleFuelStock,
  useEditVehicleFuelStock,
  VEHICLE_FUEL_STOCK_QUERY_KEY,
} from "hooks/useVehicleFuelStocks"
import { toast } from "react-toastify"
import { useQueryClient } from "@tanstack/react-query"

const VehicleFuelStockAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  fuelStock,
  fuelTypes,
  employees,
  locations,
  optionLoading,
}) => {
  const queryClient = useQueryClient()
  const { mutateAsync: createFuelStock, isPending: createLoading } =
    useCreateVehicleFuelStock()

  const { mutateAsync: editFuelStock, isPending: editLoading } =
    useEditVehicleFuelStock()

  const validationSchema = Yup.object({
    fuel_type_id: Yup.number().required("Please select fuel type"),
    fuel_station_id: Yup.number().required("Please select fuel station"),
    purchase_by_id: Yup.number().required("Please select a staff"),
    fuel_qty: Yup.number()
      .required("Please enter fuel quantity")
      .min(0, "Quantity must be >= 0"),
    fuel_price: Yup.number()
      .required("Please enter price quantity")
      .min(0, "Price must be >= 0"),
    purchase_date: Yup.date().required("Please select date"),
    remarks: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        ...values,
      }
      const action = isEdit
        ? editFuelStock({ id: fuelStock.id, ...payload })
        : createFuelStock(payload)
      try {
        const res = await action
        queryClient.invalidateQueries({
          queryKey: [VEHICLE_FUEL_STOCK_QUERY_KEY, "all"],
          exact: true, // ensures only this exact key is invalidated
        })

        toast.success(
          res?.message ??
            `Purchased Fuel ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        toast.error("Failed to save Purchased Fuel.")
      }
    },
    [isEdit, fuelStock, editFuelStock, createFuelStock, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fuel_type_id: fuelStock?.fuel_type_id || "",
      fuel_station_id: fuelStock?.fuel_station_id || "",
      purchase_by_id: fuelStock?.purchase_by_id || "",
      fuel_qty: fuelStock?.fuel_qty || "",
      fuel_price: fuelStock?.fuel_price || "",
      purchase_date: fuelStock?.purchase_date || "",
      remarks: fuelStock?.remarks || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Purchased Fuel"}${
      loading ? "..." : ""
    }`
  }, [isEdit, editLoading, createLoading])

  const isSubmitDisabled = useMemo(
    () => optionLoading || editLoading || createLoading,
    [optionLoading, editLoading, createLoading]
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Purchased Fuel" : "Add Purchased Fuel"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <Row className="mb-3">
          <Col sm={6} className="mb-3">
            <Label htmlFor="fuel_type_id">Fuel Type</Label>
            <InputField
              type="select"
              name="fuel_type_id"
              id="fuel_type_id"
              value={formik.values.fuel_type_id}
              onChange={formik.handleChange}
              invalid={
                formik.touched.fuel_type_id && !!formik.errors.fuel_type_id
              }
              loading={optionLoading}
            >
              <option value="">Select Fuel Type</option>
              {fuelTypes.map(f => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </InputField>
            {formik.touched.fuel_type_id && formik.errors.fuel_type_id && (
              <FormFeedback type="invalid">
                {formik.errors.fuel_type_id}
              </FormFeedback>
            )}
          </Col>

          <Col sm={6} className="mb-3">
            <Label htmlFor="fuel_station_id">Fuel Station</Label>
            <InputField
              type="select"
              name="fuel_station_id"
              id="fuel_station_id"
              value={formik.values.fuel_station_id}
              onChange={formik.handleChange}
              invalid={
                formik.touched.fuel_station_id &&
                !!formik.errors.fuel_station_id
              }
              loading={optionLoading}
            >
              <option value="">Select Fuel Station</option>
              {locations.map(l => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </InputField>
            {formik.touched.fuel_station_id &&
              formik.errors.fuel_station_id && (
                <FormFeedback type="invalid">
                  {formik.errors.fuel_station_id}
                </FormFeedback>
              )}
          </Col>

          <Col sm={6} className="mb-3">
            <Label htmlFor="fuel_qty">Quantity (L)</Label>
            <InputField
              type="number"
              name="fuel_qty"
              id="fuel_qty"
              value={formik.values.fuel_qty}
              onChange={formik.handleChange}
              invalid={formik.touched.fuel_qty && !!formik.errors.fuel_qty}
              loading={optionLoading}
            />
            {formik.touched.fuel_qty && formik.errors.fuel_qty && (
              <FormFeedback type="invalid">
                {formik.errors.fuel_qty}
              </FormFeedback>
            )}
          </Col>
          <Col sm={6} className="mb-3">
            <Label htmlFor="fuel_price">Price (Total)</Label>
            <InputField
              type="number"
              name="fuel_price"
              id="fuel_price"
              value={formik.values.fuel_price}
              onChange={formik.handleChange}
              invalid={formik.touched.fuel_price && !!formik.errors.fuel_price}
              loading={optionLoading}
            />
            {formik.touched.fuel_price && formik.errors.fuel_price && (
              <FormFeedback type="invalid">
                {formik.errors.fuel_price}
              </FormFeedback>
            )}
          </Col>

          <Col sm={6} className="mb-3">
            <Label htmlFor="purchase_by_id">Usage By</Label>
            <InputField
              type="select"
              name="purchase_by_id"
              id="purchase_by_id"
              value={formik.values.purchase_by_id}
              onChange={formik.handleChange}
              invalid={
                formik.touched.purchase_by_id && !!formik.errors.purchase_by_id
              }
              loading={optionLoading}
            >
              <option value="">Select a staff</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </InputField>
            {formik.touched.purchase_by_id && formik.errors.purchase_by_id && (
              <FormFeedback type="invalid">
                {formik.errors.purchase_by_id}
              </FormFeedback>
            )}
          </Col>

          <Col sm={6} className="mb-3">
            <Label htmlFor="date">Purchase Date</Label>
            <InputField
              type="datetime-local"
              name="purchase_date"
              id="purchase_date"
              value={formik.values.purchase_date}
              onChange={formik.handleChange}
              invalid={
                formik.touched.purchase_date && !!formik.errors.purchase_date
              }
              loading={optionLoading}
            />
            {formik.touched.purchase_date && formik.errors.purchase_date && (
              <FormFeedback type="invalid">
                {formik.errors.purchase_date}
              </FormFeedback>
            )}
          </Col>
        </Row>

        <div className="mb-3">
          <Label htmlFor="remarks">Remarks</Label>
          <InputField
            type="textarea"
            name="remarks"
            id="remarks"
            value={formik.values.remarks}
            onChange={formik.handleChange}
            invalid={formik.touched.remarks && !!formik.errors.remarks}
            loading={optionLoading}
          />
          {formik.touched.remarks && formik.errors.remarks && (
            <FormFeedback type="invalid">{formik.errors.remarks}</FormFeedback>
          )}
        </div>

        <div className="text-end">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default VehicleFuelStockAddEdit
